import { useAppDispatch, useAppSelector } from '@/store/hooks';
import type { ReportRow } from '@/store/slices/reportingV2/reportingV2.api';
import {
  fetchReportWidget,
  selectReportingV2Filters,
  selectReportingV2Widget,
  setReportingV2Filters,
} from '@/store/slices/reportingV2/reportingV2Slice';
import { ArrowDownwardRounded, ArrowUpwardRounded, UnfoldMoreRounded } from '@mui/icons-material';
import { Box, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { Table, TableBody } from '@mui/material';
import { useEffect, useState } from 'react';
import {
  CTR_CLICKABLE_NOTE,
  cleanName,
  clientMoneyUnavailable,
  currency,
  currencyPrecise,
  dspLabel,
  genericChannelLabel,
  num,
  percent,
  percentWhole,
  withMargin,
} from '../format';
import { useExportWidgetCsv } from '../hooks/useExportWidgetCsv';
import { pctDelta, shiftBack } from '../hooks/usePriorPeriod';
import { useReportingPermissions } from '../hooks/useReportingPermissions';
import { useResizableColumns } from '../hooks/useResizableColumns';
import { COLORS, cardSx } from '../theme';
import { GROUPING_DIM, GROUPING_LABEL, buildGroupedRequest } from '../widgets';
import ClientMoneyCoverageNote from './ClientMoneyCoverageNote';
import ColumnResizeHandle from './ColumnResizeHandle';
import Delta from './Delta';
import WidgetExportButton from './WidgetExportButton';
import WidgetSkeleton from './WidgetSkeleton';
import { SERIES } from './palette';

export const METRICS: {
  key: string;
  label: string;
  format: (v: unknown) => string;
  /** Which direction is good. Omit for 'up'; spend has no inherent polarity. */
  goodDirection?: 'up' | 'down' | 'neutral';
  /**
   * Metric key to check `restrictedFields` under, when it differs from `key`.
   * Margin isn't a backend metric — it's derived from client_spend — so it
   * must be gated under that name or a restricted role could read the sold
   * CPM back out of the margin figure. Margin also needs the wholesale-Spend
   * gate below (same as spend/cpm on ImpressionsByType, flank#4650 review) —
   * client_spend − spend otherwise lets a viewer with Client Spend but not
   * wholesale Spend back wholesale spend out of the rendered margin.
   */
  restrictedAs?: string;
  /** Shown as a header tooltip where the metric's name alone can mislead. */
  note?: string;
}[] = [
  { key: 'impressions', label: 'IMPRESSIONS', format: num },
  { key: 'spend', label: 'SPEND', format: currency, goodDirection: 'neutral' },
  // Client Spend/CPM (#4446) — what the customer is charged, distinct from
  // Spend/CPM above. Blank (never zero) where no sold CPM is on the line item.
  { key: 'client_spend', label: 'CLIENT SPEND', format: currency, goodDirection: 'neutral' },
  { key: 'client_cpm', label: 'CLIENT CPM', format: currencyPrecise, goodDirection: 'neutral' },
  // Margin (#4447) — Client Spend − Spend, derived client-side. Same blank-
  // not-zero rule as Client Spend: see withMargin in format.ts.
  { key: 'margin', label: 'MARGIN', format: currency, goodDirection: 'neutral', restrictedAs: 'client_spend' },
  { key: 'ctr', label: 'CTR', format: percent, note: CTR_CLICKABLE_NOTE },
  // Whole percent, no decimals (prep #0000) — same VCR-only decision as the KPI strip.
  { key: 'vcr', label: 'VCR', format: percentWhole },
] as const;

type SortKey = 'impressions' | 'spend' | 'client_spend' | 'client_cpm' | 'margin' | 'ctr' | 'vcr';

/** Same set ImpressionsByType's CLIENT_MONEY_KEYS gates — Client Spend, Client
 * CPM and the Margin derived from them. */
const CLIENT_MONEY_KEYS = new Set(['client_spend', 'client_cpm', 'margin']);

/**
 * #4442/#4636 — a metric the user can't see is omitted from the per-entity
 * cards and the Headline KPIs table entirely, not rendered locked. Spend
 * falls back to the role default (see useReportingPermissions); Client
 * Spend/Client CPM/Margin already have a real per-user permission, enforced
 * server-side and reported here as restrictedFields. Exported standalone so
 * the omission rule is unit-testable without mounting the card (see
 * GroupedBreakdownCard.test.ts).
 *
 * `hideClientMoney` (CodeRabbit, PR #4773): the permission alone isn't enough
 * — clientMoneyUnavailable/hasClientSpendData can still withhold these three
 * for a selection with no priced data even when canViewClientSpend is true.
 * Without this, the columns stayed selectable while only the coverage note
 * (ClientMoneyCoverageNote) was hidden, same gap PublisherInventoryTable/
 * ImpressionsByType's own visibleColumns already closes.
 */
export const visibleGroupedMetrics = (
  metrics: typeof METRICS,
  canViewWholesaleSpend: boolean,
  restrictedFields: string[],
  hideClientMoney = false,
): typeof METRICS =>
  metrics.filter(metric => {
    if ((metric.key === 'spend' || metric.key === 'margin') && !canViewWholesaleSpend) return false;
    if (hideClientMoney && CLIENT_MONEY_KEYS.has(metric.key)) return false;
    return !restrictedFields.includes(`metrics.${metric.restrictedAs ?? metric.key}`);
  });

/**
 * Grouped-dimension label. A null/blank bucket (a row whose product/channel is
 * unpopulated on the fact table) renders as an explicit "Unclassified" rather
 * than a bare "—", so a leaked unattributed bucket reads clearly instead of
 * looking like a mislabeled totals row. (The all-null case is still caught by
 * the `allNull` guard below and shown as "not yet available".)
 */
export const groupLabel = (value: unknown, grouping?: string, canViewPlatform?: boolean): string => {
  if (grouping === 'dsp') return dspLabel(value);
  const name = cleanName(value);
  const label = name === '—' ? 'Unclassified' : name;
  // GAM bakes the platform name into its channel values ("GAM Display"); every
  // other Channel-grouped widget masks that for a restricted viewer
  // (maskedChannelLabelFn) — this grouping skipped it (flank#4469).
  return grouping === 'channel' ? genericChannelLabel(label, canViewPlatform ?? true) : label;
};

const toggle = (arr: string[], value: string) => (arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value]);

/** Same shape as `toggle`, for a row that may stand for more than one raw
 * value (see `mergeMaskedChannelRows`) — select/deselect every value in the
 * group together rather than leaving a partial selection no chip reflects. */
const toggleGroup = (arr: string[], values: string[]): string[] => {
  const allSelected = values.every(v => arr.includes(v));
  return allSelected ? arr.filter(v => !values.includes(v)) : [...new Set([...arr, ...values])];
};

/** Metrics safe to sum across merged rows. `ctr`/`vcr`/`client_cpm` arrive
 * from the backend as already-computed ratios (`buildGroupedRequest` never
 * fetches their raw numerator/denominator), so there's nothing to re-pool
 * them from on a merge; `margin` is deliberately left out too and
 * recomputed afterward via `withMargin` on the merged spend/client_spend,
 * since its own blank-vs-zero rule has to re-run on the merged totals
 * rather than being summed directly. */
const ADDITIVE_METRIC_KEYS = ['impressions', 'spend', 'client_spend'] as const;

const isBlankMetric = (v: unknown): boolean => v === null || v === undefined || v === '';

/**
 * CodeRabbit, review: naively coercing a blank metric to 0 before summing
 * breaks client_spend's own blank-not-zero rule (METRICS' own comment — no
 * sold CPM on the line item, never a real zero). A merge where one side is
 * blank and the other isn't stays a real sum (0 is the correct identity for
 * the side that genuinely has no contribution there); only "every
 * contributing value is blank" stays blank. That keeps clientMoneyUnavailable
 * from mistaking a merge for priced data, but a merge of one priced row and
 * one blank row still produces a non-null, partial client_spend total — see
 * the mixed-pricing margin guard below for why that partial total can't feed
 * `withMargin` directly. Shared by the merge loop and priorRowFor so the two
 * can't drift on this.
 */
const sumMetric = (a: unknown, b: unknown): number | null =>
  isBlankMetric(a) && isBlankMetric(b) ? null : Number(isBlankMetric(a) ? 0 : a) + Number(isBlankMetric(b) ? 0 : b);

/**
 * CodeRabbit, review: `withMargin` computes margin = client_spend − spend
 * whenever both are non-blank on the merged row. A merge of one priced row
 * (client_spend: 8) and one unpriced row (client_spend: null) keeps the
 * partial total (8, per sumMetric above) rather than going blank — correct
 * for display/export, since that 8 is a real part of the picture. But
 * `spend` sums to the FULL combined total either way, so `withMargin` would
 * subtract the full spend from only a partial client_spend and show a
 * misleadingly negative margin. The coverage contract only shows Margin when
 * every impression in the row is priced, so this hidden flag — set when a
 * merge bucket mixes at least one priced and one blank client_spend row —
 * tells the caller to null margin back out after `withMargin` runs, since
 * `withMargin` itself has no way to tell a genuine total from a partial one.
 */
const MIXED_CLIENT_SPEND_PRICING_KEY = '__mixedClientSpendPricing';

export const clearMixedPricingMargin = (rows: ReportRow[]): ReportRow[] =>
  rows.map(row => (row[MIXED_CLIENT_SPEND_PRICING_KEY] ? { ...row, margin: null } : row));

/** Every raw dimension value a (possibly merged) row stands for, JSON-encoded
 * into this one field — `ReportRow` is a flat `string | number | null` index
 * signature, so a real `string[]` field can't live on it directly. Only a
 * restricted viewer's Channel breakdown ever has more than one value here;
 * everyone else gets a singleton (see `mergeMaskedChannelRows`). */
const MERGED_VALUES_KEY = '__mergedValues';

export const mergedValuesOf = (row: ReportRow): string[] => {
  try {
    const parsed = JSON.parse(String(row[MERGED_VALUES_KEY] ?? '[]'));
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
};

/**
 * A restricted viewer's Channel breakdown can have two genuinely distinct
 * backend rows — a true "Display" channel and GAM's "Display"-prefixed one
 * — mask to the same label via `groupLabel`'s `genericChannelLabel`. Found
 * live (Ivan): "Display" listed twice with different numbers, because the
 * mask only ever changed what's rendered, never re-aggregated the rows
 * behind it. The Channel filter's own dropdown already had to solve this
 * exact collision (`mergeChannelOptionsForRestrictedViewer` in format.ts);
 * this applies the same merge to the breakdown rows themselves.
 *
 * A no-op for every other grouping, and for Channel when `canViewPlatform`
 * is true — the collision only exists once the mask is actually applied.
 */
export const mergeMaskedChannelRows = (
  rows: ReportRow[],
  dimension: string,
  grouping: string | undefined,
  canViewPlatform: boolean,
): ReportRow[] => {
  if (grouping !== 'channel' || canViewPlatform) {
    return rows.map(row => ({ ...row, [MERGED_VALUES_KEY]: JSON.stringify([String(row[dimension] ?? '')]) }));
  }
  const buckets = new Map<string, ReportRow>();
  const mergedValues = new Map<string, string[]>();
  const pricing = new Map<string, { anyBlank: boolean; anyPriced: boolean }>();
  for (const row of rows) {
    const rawValue = String(row[dimension] ?? '');
    const label = groupLabel(row[dimension], grouping, canViewPlatform);
    if ('client_spend' in row) {
      const state = pricing.get(label) ?? { anyBlank: false, anyPriced: false };
      if (isBlankMetric(row.client_spend)) state.anyBlank = true;
      else state.anyPriced = true;
      pricing.set(label, state);
    }
    const existing = buckets.get(label);
    if (!existing) {
      buckets.set(label, { ...row });
      mergedValues.set(label, [rawValue]);
      continue;
    }
    mergedValues.get(label)?.push(rawValue);
    for (const key of ADDITIVE_METRIC_KEYS) {
      if (!(key in existing) && !(key in row)) continue;
      existing[key] = sumMetric(existing[key], row[key]);
    }
    existing.ctr = null;
    existing.vcr = null;
    existing.client_cpm = null;
  }
  return [...buckets.entries()].map(([label, row]) => {
    const state = pricing.get(label);
    return {
      ...row,
      [MERGED_VALUES_KEY]: JSON.stringify(mergedValues.get(label) ?? []),
      [MIXED_CLIENT_SPEND_PRICING_KEY]: state?.anyBlank && state.anyPriced ? 1 : 0,
    };
  });
};

const SortIcon = ({ col, sortCol, sortDir }: { col: SortKey; sortCol: SortKey; sortDir: 'asc' | 'desc' }) => {
  if (col !== sortCol)
    return <UnfoldMoreRounded sx={{ fontSize: 13, color: COLORS.border, verticalAlign: 'middle' }} />;
  return sortDir === 'desc' ? (
    <ArrowDownwardRounded sx={{ fontSize: 13, color: COLORS.primary, verticalAlign: 'middle' }} />
  ) : (
    <ArrowUpwardRounded sx={{ fontSize: 13, color: COLORS.primary, verticalAlign: 'middle' }} />
  );
};

const GroupedBreakdownCard = () => {
  const dispatch = useAppDispatch();
  const filters = useAppSelector(selectReportingV2Filters);
  const { rows, loading, error, restrictedFields, truncatedFields } = useAppSelector(
    selectReportingV2Widget('grouped'),
  );
  const prior = useAppSelector(selectReportingV2Widget('groupedPrior'));
  const onExportCsv = useExportWidgetCsv(
    'grouped',
    rows,
    undefined,
    undefined,
    restrictedFields.includes('metrics.client_spend'),
  );
  // #4442/#4623/#4636 — Platform cards + the Headline KPIs by Platform table
  // are suppressed entirely without "Show DSP / Platform"; Spend is omitted
  // (not shown locked) without "Show wholesale Spend and CPM".
  const { canViewPlatform, canViewWholesaleSpend, hasClientSpendData } = useReportingPermissions();
  const [sortCol, setSortCol] = useState<SortKey>(canViewWholesaleSpend ? 'spend' : 'impressions');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const { widths: colWidths, startResize } = useResizableColumns('groupedBreakdownCard');

  // sortCol is only seeded from canViewWholesaleSpend on first render — if
  // that permission is withdrawn afterward (e.g. a super-admin toggle, or a
  // slower-loading permissions fetch resolving after mount), the table kept
  // sorting by `spend`, a column it no longer renders (review, CodeRabbit).
  useEffect(() => {
    if (!canViewWholesaleSpend && sortCol === 'spend') setSortCol('impressions');
  }, [canViewWholesaleSpend, sortCol]);

  const { grouping } = filters;
  const platformGated = grouping === 'dsp' && !canViewPlatform;
  // Computed here, ahead of the dimension/early-return block below, so
  // visibleGroupedMetrics can exclude Client Spend/CPM/Margin from the
  // selectable metric list, not just the coverage note (CodeRabbit, PR
  // #4773). The merge needs a dimension key too (never meaningfully used
  // for 'all', which bails out below before anything renders).
  const rowsWithMargin = clearMixedPricingMargin(
    withMargin(
      mergeMaskedChannelRows(rows, grouping === 'all' ? '' : GROUPING_DIM[grouping], grouping, canViewPlatform),
    ),
  );
  const hideClientMoney = clientMoneyUnavailable(rowsWithMargin) || !hasClientSpendData;
  const visibleMetrics = visibleGroupedMetrics(METRICS, canViewWholesaleSpend, restrictedFields, hideClientMoney);

  // The initializer above only runs once, on mount. If permissions resolve
  // asynchronously (canViewWholesaleSpend/restrictedFields start permissive
  // and narrow after load), sortCol can keep pointing at a metric that's no
  // longer visible — sorting silently by a hidden column (CodeRabbit, #4652).
  useEffect(() => {
    if (!visibleMetrics.some(m => m.key === sortCol)) setSortCol('impressions');
  }, [visibleMetrics, sortCol]);

  // useEffect must be called unconditionally — the 'all'/platform-gated guard
  // lives inside it.
  useEffect(() => {
    if (grouping === 'all' || platformGated) return;
    const { start, end } = shiftBack(filters.dateStart, filters.dateEnd);
    dispatch(
      fetchReportWidget({
        key: 'groupedPrior',
        request: {
          ...buildGroupedRequest(grouping, filters),
          date_range: { start, end },
        },
      }),
    );
  }, [
    dispatch,
    grouping,
    filters.dateStart,
    filters.dateEnd,
    // NB: keep this list in sync with every id array buildGroupedRequest reads.
    // lineItemIds was missing, so filtering to a line item narrowed the current
    // period but left the prior-period query stale — every delta in this card
    // then compared a filtered current against an unfiltered prior.
    JSON.stringify([
      filters.dsps,
      filters.whitelabelIds,
      filters.clientIds,
      filters.advertiserIds,
      filters.ribeyeCampaignIds,
      filters.ribeyeLineItemIds,
      filters.productIds,
      filters.publishers,
      filters.channelIds,
      filters.deviceIds,
    ]),
  ]);

  if (grouping === 'all' || platformGated) return null;

  const dimension = GROUPING_DIM[grouping];
  const label = GROUPING_LABEL[grouping];
  // Same server-side signal usePublisherInventory already consumes for
  // `dimensions.ribeye_publisher` — the server flags when the GROUPED rows
  // behind this table hit its own page cap, rather than leave it to a row-
  // count check that would rarely trigger (Lucas, review). This card had no
  // consumer for it at all, so "By Channel" (and, in principle, By Platform/
  // Product) could undercount silently while Publishers warned on the same
  // condition.
  const truncated = truncatedFields.includes(`dimensions.${dimension}`);

  const priorMap = new Map<string, Record<string, unknown>>(
    withMargin(prior.rows ?? []).map(r => [String(r[dimension] ?? ''), r as Record<string, unknown>]),
  );

  // A merged current-period row (see mergeMaskedChannelRows) has to compare
  // against the SAME raw channels' combined prior-period total, not just
  // whichever one of them priorMap.get(rawValue) happens to hit — otherwise
  // the delta silently compares a merged total against a single channel's
  // prior figure. priorMap itself stays keyed by raw value (unaffected),
  // since every other grouping's lookup is already correct as-is.
  const priorRowFor = (mergedValues: string[]): Record<string, unknown> => {
    if (mergedValues.length <= 1) return priorMap.get(mergedValues[0] ?? '') ?? {};
    const merged: ReportRow = {};
    let anyBlankClientSpend = false;
    let anyPricedClientSpend = false;
    for (const value of mergedValues) {
      const p = priorMap.get(value);
      if (!p) continue;
      for (const key of ADDITIVE_METRIC_KEYS) {
        if (!(key in merged) && !(key in p)) continue;
        merged[key] = sumMetric(merged[key], p[key]);
      }
      if ('client_spend' in p) {
        if (isBlankMetric(p.client_spend)) anyBlankClientSpend = true;
        else anyPricedClientSpend = true;
      }
    }
    const [result] = withMargin([merged]);
    if (anyBlankClientSpend && anyPricedClientSpend) return { ...(result ?? merged), margin: null };
    return result ?? merged;
  };

  const allNull =
    !loading &&
    !error &&
    rows.length > 0 &&
    rows.every(r => r[dimension] === null || r[dimension] === undefined || r[dimension] === '');

  if (loading) {
    return (
      <Box sx={{ ...cardSx, p: 3 }}>
        <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 2 }}>
          {`Performance by ${label}`}
        </Typography>
        <WidgetSkeleton rows={10} />
      </Box>
    );
  }

  if (allNull) {
    return (
      <Box sx={{ ...cardSx, p: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant='subtitle1' sx={{ fontWeight: 600, color: COLORS.text }}>
          {`Performance by ${label}`}
        </Typography>
        <Typography variant='body2' sx={{ color: COLORS.muted, py: 3, textAlign: 'center' }}>
          {`${label} breakdown is not yet available — this dimension requires a data backfill.`}
        </Typography>
      </Box>
    );
  }

  const handleSort = (col: SortKey) => {
    if (col === sortCol) setSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
    else {
      setSortCol(col);
      setSortDir('desc');
    }
  };

  const sortedRows = [...rowsWithMargin].sort((a, b) => {
    const av = Number(a[sortCol] ?? 0);
    const bv = Number(b[sortCol] ?? 0);
    return sortDir === 'desc' ? bv - av : av - bv;
  });

  // review, flank#4655: this card never applied the gate ImpressionsByType
  // uses for the same note, so a non-Beeswax(-only) view, or one where
  // ribeye-api dropped client_spend for exceeding its shadow-query row cap,
  // showed a permanent, unactionable "0% … priced" note instead of nothing.
  // hideClientMoney itself is computed earlier, alongside visibleMetrics.

  const applyRowFilter = (dimValues: string[]) => {
    if (grouping === 'dsp') dispatch(setReportingV2Filters({ dsps: toggle(filters.dsps, dimValues[0]) }));
    else if (grouping === 'channel')
      dispatch(setReportingV2Filters({ channelIds: toggleGroup(filters.channelIds, dimValues) }));
    else if (grouping === 'product')
      dispatch(setReportingV2Filters({ productIds: toggle(filters.productIds, dimValues[0]) }));
  };

  return (
    <Box className='rv2-stack' sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Headline KPIs table */}
      <Box className='rv2-card' sx={{ ...cardSx, p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant='subtitle1' sx={{ fontWeight: 600, color: COLORS.text }}>
            {`Headline KPIs by ${label}`}
          </Typography>
          <WidgetExportButton onExport={onExportCsv} disabled={rows.length === 0} />
        </Box>
        {!hideClientMoney && (
          <ClientMoneyCoverageNote
            rows={rowsWithMargin}
            restricted={restrictedFields.includes('metrics.client_spend')}
          />
        )}
        <Box className='rv2-scroll' sx={{ overflowX: 'auto' }}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    color: COLORS.muted,
                    fontWeight: 700,
                    fontSize: 11,
                    letterSpacing: 0.5,
                    textTransform: 'uppercase',
                  }}
                >
                  {label.toUpperCase()}
                </TableCell>
                {visibleMetrics.map(m => (
                  <TableCell
                    key={m.key}
                    align='right'
                    sx={{
                      position: 'relative',
                      width: colWidths[m.key] ? `${colWidths[m.key]}px` : undefined,
                      color: COLORS.muted,
                      fontWeight: 700,
                      fontSize: 11,
                      letterSpacing: 0.5,
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': { color: COLORS.text },
                    }}
                    onClick={() => handleSort(m.key as SortKey)}
                  >
                    {m.note ? (
                      <Tooltip title={m.note} placement='top' arrow>
                        <span>{m.label}</span>
                      </Tooltip>
                    ) : (
                      m.label
                    )}{' '}
                    <SortIcon col={m.key as SortKey} sortCol={sortCol} sortDir={sortDir} />
                    <ColumnResizeHandle columnKey={m.key} onResizeStart={startResize} />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.map((row, i) => {
                const mergedValues = mergedValuesOf(row);
                const rawValue = mergedValues[0] ?? '';
                const displayName = groupLabel(row[dimension], grouping, canViewPlatform);
                const priorRow = priorRowFor(mergedValues);
                // rows.indexOf(row) would miss here: withMargin returns a new
                // object for any row it augments, so identity must be checked
                // against rowsWithMargin (what sortedRows was built from), not
                // the original rows array.
                const color = SERIES[rowsWithMargin.indexOf(row) % SERIES.length];
                return (
                  <TableRow
                    key={`table-${rawValue || i}-${i}`}
                    hover
                    selected={
                      !!rawValue &&
                      ((grouping === 'dsp' && filters.dsps.includes(rawValue)) ||
                        (grouping === 'channel' && mergedValues.some(v => filters.channelIds.includes(v))) ||
                        (grouping === 'product' && filters.productIds.includes(rawValue)))
                    }
                    onClick={() => {
                      // CodeRabbit, review: a blank dimension value (the
                      // "Unclassified" row) still landed in mergedValues as
                      // '', so clicking it silently added an empty-string
                      // filter that matched nothing and never showed as
                      // selected (that row is deliberately not clickable —
                      // see the `cursor`/`selected` logic above).
                      const filterValues = mergedValues.filter(v => v !== '');
                      if (filterValues.length > 0) applyRowFilter(filterValues);
                    }}
                    sx={{
                      cursor: rawValue ? 'pointer' : 'default',
                      '&:hover': { bgcolor: COLORS.pageBg },
                    }}
                  >
                    <TableCell sx={{ maxWidth: 220 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: color,
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          variant='body2'
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={displayName}
                        >
                          {displayName}
                        </Typography>
                      </Box>
                    </TableCell>
                    {visibleMetrics.map(metric => {
                      const curr = row[metric.key];
                      const prev = priorRow[metric.key];
                      // Same three-way check as the other occurrence above (CodeRabbit,
                      // flank#4446) — an empty-string metric must read "—" too, not
                      // format to a blank delta chip that looks like real data.
                      const hasMetric = curr !== null && curr !== undefined && curr !== '';
                      const delta = pctDelta(curr, prev);
                      return (
                        <TableCell key={metric.key} align='right'>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-end',
                              gap: 0.25,
                            }}
                          >
                            <Typography variant='body2' sx={{ fontWeight: metric.key === sortCol ? 700 : 400 }}>
                              {hasMetric ? metric.format(curr) : '—'}
                            </Typography>
                            {hasMetric && delta !== null ? (
                              <Delta value={delta} goodDirection={metric.goodDirection} />
                            ) : (
                              <Typography variant='caption' sx={{ color: COLORS.muted }}>
                                — —
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
        {truncated && (
          <Typography variant='caption' sx={{ color: COLORS.muted, mt: 1.5, display: 'block' }}>
            This selection has more {label.toLowerCase()}s than the report can fetch in one page — totals for the
            smallest ones may be undercounted. Narrow your filters to see the full picture.
          </Typography>
        )}
        <Typography variant='caption' sx={{ color: COLORS.muted, mt: 1.5, display: 'block' }}>
          Click a row to filter by that {label.toLowerCase()}.
        </Typography>
      </Box>
    </Box>
  );
};

export default GroupedBreakdownCard;
